import { Interaction } from "@/service/Model";
import PostMessageService from "@/service/PostMessage";

export interface TrackingEvent {
  trigger: string;
  feature: string;
  action: string;
  label: string;
  variant: string;
  code?: string;
}

export default class TrackingService {
  private static instance: TrackingService;
  private postMessageService: PostMessageService;
  private _interaction: Interaction;

  public static getInstance(): TrackingService {
    if (!TrackingService.instance) {
      TrackingService.instance = new TrackingService();
      TrackingService.instance.postMessageService = new PostMessageService(
        window.parent
      );
    }
    return TrackingService.instance;
  }

  set interaction(value: Interaction) {
    this._interaction = value;
  }

  public trackOnInViewport(): void {
    if (!this._interaction) {
      return;
    }
    const { title, type, layout } = this._interaction;
    this.track({
      feature: "interaction poll",
      label: title,
      variant: `${type}.${layout}`,
      trigger: "onLoad",
      action: "view",
    });
  }

  private trackNps(data: Partial<TrackingEvent>): void {
    if (!this._interaction) {
      return;
    }
    const { title } = this._interaction;
    const event: TrackingEvent = {
      feature: "nps",
      variant: title,
      label: "",
      trigger: "",
      action: "",
    };
    this.track({ ...event, ...data });
  }

  public trackNpsView(): void {
    this.trackNps({ action: "view" });
  }

  public trackNpsExit(): void {
    this.trackNps({ action: "exit" });
  }

  public trackNpsClose(): void {
    this.trackNps({ action: "close" });
  }

  public trackNpsCloseBg(): void {
    this.trackNps({ action: "close-bg" });
  }

  public trackNpsVote(answer: number): void {
    this.trackNps({ action: "vote", label: String(answer) });
  }

  public trackNpsSelection(answer: number): void {
    this.trackNps({ action: "selection", label: String(answer) });
  }

  public trackNpsFeedback(message: string): void {
    this.trackNps({ action: "feedback", label: message });
  }

  public trackOnVote(): void {
    if (!this._interaction) {
      return;
    }
    const { title, type, layout } = this._interaction;
    this.track({
      feature: "interaction poll", // This was used everywhere in readerui, also for meter and compass
      label: title,
      variant: `${type}.${layout}`, // The dot notation is new e.g. poll.image
      trigger: "onClick", // "onClick" was used everywhere
      action: "click", // "click" was used everywhere
    });
  }

  public trackOnOutroImpression(): void {
    if (!this._interaction) {
      return;
    }
    const { outro } = this._interaction;
    this.track({
      feature: "interaction poll",
      label: "outro",
      variant: outro!.outroType,
      trigger: "onLoad",
      action: "impression",
    });
  }

  public trackOnOutroClick(): void {
    if (!this._interaction) {
      return;
    }
    const { outro } = this._interaction;
    this.track({
      feature: "interaction poll",
      label: "outro",
      variant: outro!.outroType,
      trigger: "onClick",
      action: "click",
    });
  }

  public trackOnPromoImpression(code: string): void {
    if (!this._interaction) {
      return;
    }
    const { outro } = this._interaction;
    this.track({
      feature: "interaction poll",
      label: "promo",
      variant: outro!.outroType,
      trigger: "onLoad",
      action: "impression",
      code,
    });
  }

  public trackOnPromoClick(code: string): void {
    if (!this._interaction) {
      return;
    }
    const { outro } = this._interaction;
    this.track({
      feature: "interaction poll",
      label: "promo",
      variant: outro!.outroType,
      trigger: "onClick",
      action: "click",
      code,
    });
  }

  private track(event: TrackingEvent): void {
    if (__PROD__) {
      this.postMessageService.send({ message: "tracking", data: event });
    }
  }
}
